import { Component, ElementRef, Input, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';

import { sleep } from '../../../classes/sleep';
import { ESRI } from '../../../esri-loader-util';
import { InputConverter } from '../../../inputConverter';
import { BaseComponent } from '../../base.component';

let Expand: __esri.ExpandConstructor;

const bottomOffsetHeight = 30;
const retrySizeSetupTime = 50;
const retrySizeSetup = 5;

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-expander',
    styleUrls: ['../../sharedStyles/base.scss', './expander.component.scss'],
    templateUrl: './expander.component.html'
})
export class ExpanderComponent extends BaseComponent {
    @ViewChild('expander') private readonly expanderRef: ElementRef<HTMLDivElement>;
    @ViewChild('expanderContent') private readonly expanderContentRef: ElementRef<HTMLDivElement>;

    @Input() collapseIconClass: string;
    @Input() expandIconClass: string;
    @Input() collapseTooltip: string;
    @Input() expandTooltip: string;
    @Input() group: string;
    @Input() mode: 'auto' | 'floating' | 'drawer' = 'auto';
    @Input() expand: NonNullable<__esri.Expand>;
    @Input() mapViewRef: NonNullable<__esri.MapView>;

    @Input()
    @InputConverter()
    expanded: boolean;
    @Input()
    @InputConverter()
    autoCollapse: boolean;
    @Input()
    @InputConverter()
    iconNumber: number;

    cascadeClasses: string[] = [];

    constructor(_vcr: ViewContainerRef) {
        super(_vcr);
        this.loadExpander();
    }

    private async loadExpander() {
        try {
            await this.setMapViewRef();
            await this.createExpander();
        }
        catch (err) {
            this.readyProblem(err);
        }
        finally {
            this.isReady();
        }
    }

    private async setMapViewRef() {
        if (!this.mapViewRef) {
            const parent = await this.getClosestAncestor('mc-map');
            if (parent) {
                await parent.ready;
                this.mapViewRef = parent.mapView;
                this.setupClasses();
            } else {
                this.readyProblem('Map View Reference not supplied and reference from parent element could not be retrieved.');
            }
        }
    }

    private setupClasses() {
        let lastChecked = this.hostElement.assignedSlot ? this.hostElement.assignedSlot.parentElement : this.hostElement.parentElement;
        let allUIClasses = this.mapViewRef.container.className.split(' ').filter(x => x.startsWith('esri-view-'));
        allUIClasses = allUIClasses.concat(lastChecked.className.split(' ').filter(x => x.startsWith('esri-ui-')));
        let lastClasses: string[] = allUIClasses.concat([]);
        while (lastClasses.length) {
            lastChecked = lastChecked.parentElement;
            lastClasses = lastChecked.className.split(' ').filter(x => x.startsWith('esri-ui-'));
            allUIClasses = allUIClasses.concat(lastClasses);
        }
        this.cascadeClasses = this.cascadeClasses.concat(allUIClasses);
    }

    private async setupSize(count = 0) {
        const content = this.getExpandContent();
        if (content) {
            content.style.maxHeight = this.getCurrentViewHeight();
            this.mapViewRef.on('resize', () => {
                this.getExpandContent().style.maxHeight = this.getCurrentViewHeight();
            });
        } else if (count < retrySizeSetup) {
            await sleep(retrySizeSetupTime);
            this.setupSize(count++);
        }
    }

    private getExpandContent() {
        return this.expanderRef.nativeElement.querySelector('.esri-expand .esri-expand__content') as HTMLDivElement;
    }

    private getCurrentViewHeight() {
        return `${(this.mapViewRef.container.offsetHeight - bottomOffsetHeight)}px`;
    }

    private async createExpander(): Promise<any> {
        [Expand] = await Promise.all([ESRI.Expand]);
        this.expand = new Expand({
            collapseIconClass: this.collapseIconClass,
            container: this.expanderRef.nativeElement,
            content: this.expanderContentRef.nativeElement,
            expanded: this.expanded,
            expandIconClass: this.expandIconClass,
            view: this.mapViewRef,
            autoCollapse: this.autoCollapse,
            collapseTooltip: this.collapseTooltip,
            expandTooltip: this.expandTooltip,
            mode: this.mode,
            group: this.group,
            iconNumber: this.iconNumber
        });
        await this.setupSize();
    }
}
